import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Plus, UserPlus, Pencil, Trash2, RotateCcw, Building2, KeyRound, X, Send, MailX } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { getErrorMessage } from "../../../api/client";
import * as usersApi from "../../../api/users";
import { CreateInvitoPayload, CreateOperatorPayload, ClienteLite, InvitoPending } from "../../../api/users";
import { ClientiMultiSelect } from "../../../components/ClientiMultiSelect";
import * as anagraficaApi from "../../../api/anagrafica";
import { CentroAssistenzaOption, IstitutoVigilanzaOption } from "../../../api/anagrafica";
import { Role, User } from "../../../types";
import { Table, TableColumn } from "../../../components/ui/Table";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";

const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  staff: "Staff Evo",
  centro_assistenza: "Centro Assistenza",
  istituto_vigilanza: "Istituto di Vigilanza",
  operatore_tecnico: "Operatore Tecnico",
  operatore_vigilanza: "Operatore Vigilanza",
  cliente: "Cliente",
};

/**
 * Una riga della tabella Utenti: o un utente registrato, o un invito ancora in
 * sospeso.
 *
 * Stanno insieme perché sono la stessa domanda — "chi ha accesso, o sta per
 * averlo" — e finché gli inviti non c'erano la risposta era falsa: una persona
 * invitata non compariva da nessuna parte fino a registrazione avvenuta, e
 * l'unico segnale della sua esistenza era l'errore che si otteneva provando a
 * invitarla di nuovo.
 *
 * Unione discriminata e non un `User` con campi finti: un invito non ha un
 * ultimo accesso, non ha un telefono e non si può disabilitare. Il
 * discriminante costringe ogni colonna a dire cosa mostra nei due casi, invece
 * di lasciar passare un "—" per distrazione.
 */
type RigaUtenti =
  | { tipo: "utente"; id: string; utente: User }
  | { tipo: "invito"; id: string; invito: InvitoPending };

/**
 * Lo stato di un invito come si legge in tabella. Sono due parole diverse
 * perché sono due situazioni diverse: "Invitato" aspetta ancora qualcuno,
 * "Invito scaduto" no — il link non funziona più e va ripetuto.
 *
 * In un posto solo perché la usano sia il badge che l'ordinamento della colonna
 * Stato, e ordinare su una parola diversa da quella scritta darebbe una lista
 * ordinata in un modo che non si vede.
 */
function etichettaInvito(invito: InvitoPending): string {
  return invito.scaduto ? "Invito scaduto" : "Invitato";
}

export default function Users() {
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();

  const [users, setUsers] = useState<User[]>([]);
  const [inviti, setInviti] = useState<InvitoPending[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [operatorOpen, setOperatorOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [clientiUser, setClientiUser] = useState<User | null>(null);
  const [permsUser, setPermsUser] = useState<User | null>(null);
  // Traccia per id quali richieste (reset password / disabilitazione) sono in
  // corso, per disabilitare il bottone e mostrare uno spinner — evita che un
  // doppio click invii due email/SMS con due password temporanee diverse.
  const [pendingActionIds, setPendingActionIds] = useState<Set<string>>(new Set());

  function markPending(id: string, pending: boolean) {
    setPendingActionIds((prev) => {
      const next = new Set(prev);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  // Gli inviti li vede (e li gestisce) solo l'admin: `/api/inviti` è dietro
  // `utenti:amministrazione`, come l'invio. Per un Centro Assistenza o un
  // Istituto di Vigilanza la pagina resta esattamente quella di prima.
  const canInvite = currentUser?.role === "admin";

  async function loadUsers() {
    setIsLoading(true);
    try {
      // In parallelo, ma con esiti separati: se cade la lista inviti la lista
      // utenti deve comparire lo stesso — è la ragione principale per cui si
      // apre questa pagina, e perderla per un errore su un elenco accessorio
      // sarebbe un peggioramento netto.
      const [utentiRes, invitiRes] = await Promise.allSettled([
        usersApi.listUsers(),
        canInvite ? usersApi.listInviti() : Promise.resolve({ inviti: [] }),
      ]);

      if (utentiRes.status === "fulfilled") {
        setUsers(utentiRes.value.users);
      } else {
        showToast(getErrorMessage(utentiRes.reason, "Impossibile caricare la lista utenti."), "error");
      }

      if (invitiRes.status === "fulfilled") {
        setInviti(invitiRes.value.inviti);
      } else {
        showToast(getErrorMessage(invitiRes.reason, "Impossibile caricare gli inviti in sospeso."), "error");
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canInvite]);

  async function handleDisable(user: User) {
    const confirmed = window.confirm(`Disabilitare l'account di ${user.full_name ?? user.email}?`);
    if (!confirmed) return;

    markPending(user.id, true);
    try {
      await usersApi.disableUser(user.id);
      showToast("Utente disabilitato.", "success");
      loadUsers();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile disabilitare l'utente."), "error");
    } finally {
      markPending(user.id, false);
    }
  }

  async function handleReactivate(user: User) {
    markPending(user.id, true);
    try {
      await usersApi.reactivateUser(user.id);
      showToast("Utente riabilitato.", "success");
      loadUsers();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile riabilitare l'utente."), "error");
    } finally {
      markPending(user.id, false);
    }
  }

  async function handleResetPassword(user: User) {
    // Mancava del tutto: senza conferma, un click accidentale (o un doppio
    // click involontario) reimposta la password e invia nuove credenziali
    // via email/SMS senza che nessuno lo abbia davvero deciso.
    const confirmed = window.confirm(
      `Reimpostare la password di ${user.full_name ?? user.email}? Le nuove credenziali verranno inviate via email/SMS.`
    );
    if (!confirmed) return;

    markPending(user.id, true);
    try {
      const result = await usersApi.resetPassword(user.id);
      if (result.temp_password) {
        // Niente scomparsa automatica: è l'unico posto in cui questa password
        // compare, non è recuperabile dopo, e il messaggio stesso chiede di
        // comunicarla a mano — tre secondi non bastano per trascriverla.
        showToast(
          `Email e SMS non recapitati. Password temporanea da comunicare manualmente: ${result.temp_password}`,
          "warning",
          { persistent: true, copyValue: result.temp_password }
        );
      } else {
        const canali = [
          result.notifica_inviata.email && "email",
          result.notifica_inviata.sms && "SMS",
        ].filter(Boolean);
        showToast(`Nuova password inviata via ${canali.join(" e ")}.`, "success");
      }
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile reimpostare la password."), "error");
    } finally {
      markPending(user.id, false);
    }
  }

  async function handleRipetiInvito(invito: InvitoPending) {
    // Ripetere spedisce una email e invalida il link precedente: se la persona
    // stava proprio compilando la registrazione, il suo link muore a metà.
    const confirmed = window.confirm(
      `Inviare di nuovo l'invito a ${invito.email}? Il link precedente smetterà di funzionare.`
    );
    if (!confirmed) return;

    markPending(invito.id, true);
    try {
      const result = await usersApi.resendInvito(invito.id);
      if (result.email_sent) {
        showToast(result.message, "success");
      } else {
        // Stesso criterio del reset password: se l'email non è partita, il link
        // è l'unica cosa che permette comunque di completare l'invito, non
        // ricompare da nessun'altra parte, e va copiato a mano. Un toast che
        // sparisce dopo tre secondi lo perderebbe.
        showToast(`${result.message} Link: ${result.registration_link}`, "warning", {
          persistent: true,
          copyValue: result.registration_link,
        });
      }
      loadUsers();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile ripetere l'invito."), "error");
    } finally {
      markPending(invito.id, false);
    }
  }

  async function handleAnnullaInvito(invito: InvitoPending) {
    const confirmed = window.confirm(
      `Annullare l'invito a ${invito.email}? Il link non funzionerà più e l'indirizzo tornerà invitabile.`
    );
    if (!confirmed) return;

    markPending(invito.id, true);
    try {
      await usersApi.cancelInvito(invito.id);
      showToast("Invito annullato.", "success");
      loadUsers();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile annullare l'invito."), "error");
    } finally {
      markPending(invito.id, false);
    }
  }

  const columns: TableColumn<RigaUtenti>[] = [
    {
      key: "full_name",
      header: "Nome",
      // È la colonna che identifica la riga: non si può spegnere, altrimenti
      // resterebbe un elenco di ruoli e stati senza sapere di chi.
      sempreVisibile: true,
      // Si ordina sul nome, ma quando manca si ricade sul riferimento: è quello
      // che si legge in tabella, e senza il ripiego tutti gli account aziendali
      // finirebbero insieme in fondo come "senza dato". Un invito un nome non
      // ce l'ha ancora — lo sceglie chi si registra — quindi è `null` e finisce
      // in fondo come tutte le righe senza il dato.
      sortValue: (r) => (r.tipo === "utente" ? r.utente.full_name ?? r.utente.riferimento : null),
      // Il riferimento compare sotto il nome: sugli account aziendali (casella
      // generica, nome vuoto) è l'unica cosa che distingue due utenti della
      // stessa azienda. Sugli inviti, al suo posto, chi l'ha mandato: davanti a
      // un invito che non ricordi è la prima cosa da sapere.
      render: (r) =>
        r.tipo === "utente" ? (
          <div className="flex flex-col">
            <span>{r.utente.full_name ?? "—"}</span>
            {r.utente.riferimento && <span className="text-xs text-ink/50">{r.utente.riferimento}</span>}
          </div>
        ) : (
          <div className="flex flex-col">
            <span className="text-ink/40">— non ancora registrato</span>
            {r.invito.invitato_da && (
              <span className="text-xs text-ink/50">Invitato da {r.invito.invitato_da}</span>
            )}
          </div>
        ),
    },
    {
      key: "email",
      header: "Email",
      // Il segnaposto <uuid>@no-email... vuol dire "nessuna email": mostrarlo
      // per esteso riempirebbe la colonna di identificativi interni.
      //
      // Vale anche per l'ordinamento: `null` manda quegli account in fondo come
      // "senza email", mentre ordinare sul segnaposto li mescolerebbe alle
      // email vere seguendo un uuid che nessuno vede.
      sortValue: (r) =>
        r.tipo === "invito"
          ? r.invito.email
          : /@no-email\./i.test(r.utente.email)
          ? null
          : r.utente.email,
      render: (r) =>
        r.tipo === "invito" ? (
          // Su un invito l'email è l'unica identità che c'è: è l'indirizzo a
          // cui è andato il link, ed è il valore che blocca un secondo invito.
          <span className="font-medium">{r.invito.email}</span>
        ) : /@no-email\./i.test(r.utente.email) ? (
          <span className="text-ink/40">— nessuna</span>
        ) : (
          r.utente.email
        ),
    },
    {
      key: "role",
      header: "Ruolo",
      // Sull'etichetta, non sulla chiave: "Centro assistenza" e "Op. tecnico"
      // si ordinano come si leggono, non come stanno nell'enum.
      sortValue: (r) => ROLE_LABELS[r.tipo === "utente" ? r.utente.role : r.invito.role],
      // Anche per un invito il ruolo è già deciso (lo sceglie chi invita, e la
      // registrazione non lo cambia): si mostra uguale, senza distinzioni.
      render: (r) => (
        <Badge color="primary">{ROLE_LABELS[r.tipo === "utente" ? r.utente.role : r.invito.role]}</Badge>
      ),
    },
    {
      key: "is_active",
      header: "Stato",
      // Sulla stessa parola che si vede: "Attivo" prima di "Disabilitato" in
      // ordine crescente, che è anche l'ordine utile. Gli inviti entrano nella
      // stessa scala con la loro etichetta.
      sortValue: (r) => (r.tipo === "utente" ? (r.utente.is_active ? "Attivo" : "Disabilitato") : etichettaInvito(r.invito)),
      render: (r) => {
        if (r.tipo === "utente") {
          return (
            <Badge color={r.utente.is_active ? "success" : "danger"}>
              {r.utente.is_active ? "Attivo" : "Disabilitato"}
            </Badge>
          );
        }
        // Le due righe sotto il badge sono il motivo per cui questa lista
        // esiste: dicono se il link è ancora buono e — soprattutto — se
        // l'email è davvero partita. Un invito con `email_sent = false` è un
        // record che blocca l'indirizzo senza che nessuno abbia mai ricevuto
        // niente, ed è indistinguibile dagli altri finché non lo si scrive.
        return (
          <div className="flex flex-col gap-0.5">
            <Badge color={r.invito.scaduto ? "danger" : "warning"}>{etichettaInvito(r.invito)}</Badge>
            <span className="text-xs text-ink/50">
              {r.invito.scaduto ? "Scaduto il " : "Scade il "}
              {new Date(r.invito.expires_at).toLocaleDateString("it-IT")}
            </span>
            {!r.invito.email_sent && (
              <span className="text-xs font-medium text-[#c2410c]">Email non recapitata</span>
            )}
          </div>
        );
      },
    },
    {
      key: "last_login",
      header: "Ultimo accesso",
      // La stringa ISO si ordina già cronologicamente. Chi non ha mai fatto
      // accesso ha `null` e finisce in fondo in entrambi i versi: "Mai" non è
      // una data antichissima, è l'assenza di una data. Un invito non ha un
      // accesso da poter mancare: `null` anche lui.
      sortValue: (r) => (r.tipo === "utente" ? r.utente.last_login : null),
      render: (r) =>
        r.tipo === "utente" ? (
          r.utente.last_login ? (
            new Date(r.utente.last_login).toLocaleString("it-IT")
          ) : (
            "Mai"
          )
        ) : (
          <span className="text-ink/30">—</span>
        ),
    },
    {
      key: "organizzazione",
      header: "Organizzazione",
      // Spenta di default, e i numeri dicono perché: in produzione è
      // valorizzata su 3 utenti su 10. Non è un dato mancante — admin, staff e
      // clienti non appartengono a nessuna organizzazione, quindi per loro è
      // giusto che sia vuota. Serve a chi gestisce operatori, che la accende
      // una volta e se la ritrova.
      nascostaDiDefault: true,
      sortValue: (r) => (r.tipo === "utente" ? r.utente.organizzazione ?? null : r.invito.organizzazione),
      render: (r) => {
        const org = r.tipo === "utente" ? r.utente.organizzazione : r.invito.organizzazione;
        return org ?? <span className="text-ink/30">—</span>;
      },
    },
    {
      key: "telefono",
      header: "Telefono",
      nascostaDiDefault: true,
      // L'invito non porta un telefono: il Flusso A si fa per email, e il
      // numero lo scrive semmai l'utente dopo la registrazione.
      sortValue: (r) => (r.tipo === "utente" ? r.utente.telefono : null),
      render: (r) =>
        r.tipo === "utente" && r.utente.telefono ? r.utente.telefono : <span className="text-ink/30">—</span>,
    },
    {
      key: "login_method",
      header: "Accesso",
      // Con che cosa entra: gli operatori creati senza email entrano col
      // telefono, ed è l'unica spiegazione del "— nessuna" nella colonna Email.
      // Su un invito non c'è ancora niente con cui entrare.
      nascostaDiDefault: true,
      sortValue: (r) => (r.tipo === "utente" ? (r.utente.login_method === "telefono" ? "Telefono" : "Email") : null),
      render: (r) =>
        r.tipo === "utente" ? (
          <span className="text-xs text-ink/60">{r.utente.login_method === "telefono" ? "Telefono" : "Email"}</span>
        ) : (
          <span className="text-ink/30">—</span>
        ),
    },
    {
      key: "created_at",
      header: "Creato il",
      nascostaDiDefault: true,
      // Per un invito è la data in cui è stato mandato: nella stessa colonna
      // perché risponde alla stessa domanda, "da quando esiste questa riga".
      sortValue: (r) => (r.tipo === "utente" ? r.utente.created_at : r.invito.created_at),
      render: (r) =>
        new Date(r.tipo === "utente" ? r.utente.created_at : r.invito.created_at).toLocaleDateString("it-IT"),
    },
    {
      key: "actions",
      header: "Azioni",
      // Colonna di comandi: senza, la pagina perde ogni modo di agire sugli
      // utenti e non resta niente che spieghi come riaccenderla.
      sempreVisibile: true,
      render: (r) => {
        // Su un invito quasi nessuna azione da utente ha senso: non c'è una
        // password da reimpostare, un account da disabilitare né permessi da
        // assegnare. Restano le due che riguardano l'invito in quanto tale.
        if (r.tipo === "invito") {
          return (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                loading={pendingActionIds.has(r.invito.id)}
                onClick={() => handleRipetiInvito(r.invito)}
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                Ripeti invito
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={pendingActionIds.has(r.invito.id)}
                onClick={() => handleAnnullaInvito(r.invito)}
              >
                <MailX className="h-4 w-4" aria-hidden="true" />
                Annulla invito
              </Button>
            </div>
          );
        }

        const u = r.utente;
        return (
          <div className="flex flex-wrap gap-2">
            {currentUser?.role === "admin" && (
              <Button size="sm" variant="secondary" onClick={() => setEditingUser(u)}>
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Modifica
              </Button>
            )}
            {currentUser?.role === "admin" && u.role === "cliente" && (
              <Button size="sm" variant="secondary" onClick={() => setClientiUser(u)}>
                <Building2 className="h-4 w-4" aria-hidden="true" />
                Aziende
              </Button>
            )}
            {currentUser?.role === "admin" && (
              <Button size="sm" variant="secondary" onClick={() => setPermsUser(u)}>
                <KeyRound className="h-4 w-4" aria-hidden="true" />
                Permessi
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              loading={pendingActionIds.has(u.id)}
              onClick={() => handleResetPassword(u)}
            >
              Reset password
            </Button>
            {u.is_active ? (
              <Button
                size="sm"
                variant="ghost"
                loading={pendingActionIds.has(u.id)}
                onClick={() => handleDisable(u)}
              >
                Disabilita
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                loading={pendingActionIds.has(u.id)}
                onClick={() => handleReactivate(u)}
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                Riabilita
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  // Gli inviti PRIMA degli utenti, e non per ordine cronologico: sono le uniche
  // righe che chiedono una decisione (ripetere o annullare), e in fondo a un
  // elenco ordinato per data resterebbero invisibili quasi quanto prima. Chi
  // vuole un altro ordine clicca un'intestazione: la tabella è ordinabile.
  const righe: RigaUtenti[] = [
    ...inviti.map((i): RigaUtenti => ({ tipo: "invito", id: i.id, invito: i })),
    ...users.map((u): RigaUtenti => ({ tipo: "utente", id: u.id, utente: u })),
  ];

  const canCreateOperator =
    currentUser?.role === "admin" || currentUser?.role === "centro_assistenza" || currentUser?.role === "istituto_vigilanza";

  return (
    <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Utenti</h1>
          <p className="mt-1 text-sm text-ink/50">
            {currentUser?.role === "admin"
              ? "Tutti gli utenti della piattaforma."
              : "Gli operatori che gestisci direttamente."}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {canCreateOperator && (
            <Button variant="secondary" onClick={() => setOperatorOpen(true)}>
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              Aggiungi operatore
            </Button>
          )}
          {canInvite && (
            <Button onClick={() => setInviteOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Invita
            </Button>
          )}
        </div>
      </header>

      <Table
        columns={columns}
        data={righe}
        // Prefissato col tipo: gli id vengono da due tabelle diverse e restare
        // univoci fra loro non è qualcosa che il database garantisca.
        rowKey={(r) => `${r.tipo}:${r.id}`}
        isLoading={isLoading}
        emptyMessage="Nessun utente."
        ordinabile
        sceltaColonne="utenti"
      />

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} onCreated={loadUsers} />
      <CreateOperatorModal
        open={operatorOpen}
        onClose={() => setOperatorOpen(false)}
        onCreated={loadUsers}
        callerRole={currentUser?.role}
      />
      <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} onSaved={loadUsers} />
      <ClientiUserModal user={clientiUser} onClose={() => setClientiUser(null)} />
      <PermissionsModal user={permsUser} onClose={() => setPermsUser(null)} />
    </div>
  );
}

// ============================================================================
// Modal — modifica anagrafica utente (solo admin). Il ruolo è in sola lettura.
// ============================================================================
interface EditUserForm {
  full_name: string;
  email: string;
  telefono: string;
  note: string;
  centro_assistenza_id: string;
  istituto_vigilanza_id: string;
  login_method: "email" | "telefono";
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: User | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { showToast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditUserForm>();

  const [centri, setCentri] = useState<CentroAssistenzaOption[]>([]);
  const [istituti, setIstituti] = useState<IstitutoVigilanzaOption[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const showCentro = user?.role === "centro_assistenza" || user?.role === "operatore_tecnico";
  const showIstituto = user?.role === "istituto_vigilanza" || user?.role === "operatore_vigilanza";

  // Il login via telefono è il canale del Flusso B (credenziali via SMS) ed è
  // riservato agli operatori. Se però un record si trova già su 'telefono' con
  // un altro ruolo, l'opzione resta abilitata: disabilitarla mostrerebbe una
  // scelta diversa da quella reale, e il backend lascia comunque passare il
  // valore invariato.
  const isOperatore = user?.role === "operatore_tecnico" || user?.role === "operatore_vigilanza";
  const phoneLoginAllowed = isOperatore || user?.login_method === "telefono";

  useEffect(() => {
    if (!user) return;
    reset({
      full_name: user.full_name ?? "",
      // Un segnaposto <uuid>@no-email... significa "nessuna email": mostrarlo
      // nel campo esporrebbe un id interno come se fosse un indirizzo vero.
      email: /@no-email\./i.test(user.email) ? "" : user.email,
      telefono: user.telefono ?? "",
      note: user.note ?? "",
      centro_assistenza_id: user.centro_assistenza_id ?? "",
      istituto_vigilanza_id: user.istituto_vigilanza_id ?? "",
      login_method: user.login_method,
    });
    if (showCentro) anagraficaApi.listCentriAssistenza().then(({ centri }) => setCentri(centri));
    if (showIstituto) anagraficaApi.listIstitutiVigilanza().then(({ istituti }) => setIstituti(istituti));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function onSubmit(values: EditUserForm) {
    if (!user) return;
    const email = values.email.trim();
    const telefono = values.telefono.trim();

    // Deve restare valorizzata la credenziale del canale con cui l'utente
    // accede: il login cerca solo su quello, quindi un'email valida non salva
    // chi ha login_method = 'telefono' e il telefono vuoto. Il placeholder
    // <uuid>@no-email... non conta come email reale. Il backend ripete lo
    // stesso controllo, questo è solo per un feedback immediato.
    const emailIsPlaceholder = /@no-email\./i.test(email);
    if (values.login_method === "email" && (!email || emailIsPlaceholder)) {
      showToast("L'accesso è impostato su email: inserisci un'email reale, oppure scegli l'accesso via telefono.", "error");
      return;
    }
    if (values.login_method === "telefono" && !telefono) {
      showToast("L'accesso è impostato su telefono: inserisci un numero, oppure scegli l'accesso via email.", "error");
      return;
    }

    try {
      // role NON viene mai incluso nel payload, nemmeno greyed-out.
      await usersApi.updateUser(user.id, {
        full_name: values.full_name.trim() || null,
        // Vuota = richiesta di azzeramento, e va inviata come tale: il backend
        // la traduce nel segnaposto <uuid>@no-email... (la colonna è NOT NULL).
        // Prima veniva rimossa dal payload, quindi l'azzeramento si perdeva qui
        // e il toast confermava un aggiornamento che non era avvenuto.
        email,
        telefono: telefono || null,
        note: values.note.trim() || null,
        centro_assistenza_id: showCentro ? values.centro_assistenza_id || null : undefined,
        istituto_vigilanza_id: showIstituto ? values.istituto_vigilanza_id || null : undefined,
        login_method: values.login_method,
      });
      showToast("Utente aggiornato.", "success");
      onClose();
      onSaved();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aggiornare l'utente."), "error");
    }
  }

  async function handlePermanentDelete() {
    if (!user) return;
    const ok = window.confirm(
      `Eliminare DEFINITIVAMENTE l'utente ${user.full_name ?? user.email}?\n\nL'operazione non è reversibile. Se l'utente ha record collegati verrà bloccata.`
    );
    if (!ok) return;

    setIsDeleting(true);
    try {
      await usersApi.deleteUserPermanent(user.id);
      showToast("Utente eliminato definitivamente.", "success");
      onClose();
      onSaved();
    } catch (err) {
      // Il backend risponde 409 con `dettaglio`: elenca i record collegati e
      // suggerisce di disabilitare invece di eliminare.
      const dettaglio = (err as { response?: { data?: { dettaglio?: Record<string, number> } } }).response?.data
        ?.dettaglio;
      if (dettaglio) {
        const parts = Object.entries(dettaglio)
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${k.replace(/_/g, " ")}: ${n}`);
        showToast(
          `Impossibile eliminare: l'utente ha record collegati (${parts.join(", ")}). Disabilitalo invece.`,
          "error"
        );
      } else {
        showToast(getErrorMessage(err, "Impossibile eliminare l'utente."), "error");
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Modal
      open={user !== null}
      onClose={onClose}
      title="Modifica utente"
      size="sm"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <Button type="button" variant="danger" onClick={handlePermanentDelete} loading={isDeleting}>
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Elimina definitivamente
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button type="submit" form="edit-user-form" loading={isSubmitting}>
              Salva
            </Button>
          </div>
        </div>
      }
    >
      {user && (
        <form id="edit-user-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
          <Input
            label="Nome completo"
            error={errors.full_name?.message}
            {...register("full_name")}
          />
          <Input
            label="Email"
            type="email"
            hint="Obbligatoria solo se l'utente accede via email. Lasciandola vuota l'utente risulta senza email."
            error={errors.email?.message}
            {...register("email")}
          />
          {/* Il formato va detto prima, non solo quando il salvataggio fallisce:
              il backend rifiuta un numero senza prefisso internazionale e non
              lo deduce, per non attribuire per errore all'Italia un numero estero. */}
          <Input
            label="Telefono"
            hint="In formato internazionale, con il prefisso del Paese: es. +393351234567."
            {...register("telefono")}
          />

          {/* Nessun defaultChecked: il valore corrente arriva dal reset() sopra. */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">Metodo di accesso</label>
            <div className="flex gap-4 text-sm text-ink">
              <label className="flex items-center gap-2">
                <input type="radio" value="email" {...register("login_method")} />
                Email
              </label>
              <label
                className={`flex items-center gap-2 ${phoneLoginAllowed ? "" : "cursor-not-allowed text-ink/35"}`}
              >
                <input
                  type="radio"
                  value="telefono"
                  disabled={!phoneLoginAllowed}
                  {...register("login_method")}
                />
                Telefono
              </label>
            </div>
            <p className="text-xs text-ink/40">
              {phoneLoginAllowed
                ? "Con quale credenziale l'utente effettua il login. Cambiandolo, l'altra credenziale smette di funzionare per l'accesso e resta solo per contatto/notifiche: avvisa l'utente."
                : "L'accesso via telefono è disponibile solo per Operatore Tecnico e Operatore Vigilanza: per gli altri ruoli il login resta via email."}
            </p>
          </div>

          {showCentro && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">Centro Assistenza collegato</label>
              <select
                className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                {...register("centro_assistenza_id")}
              >
                <option value="">Nessuno</option>
                {centri.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          {showIstituto && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">Istituto di Vigilanza collegato</label>
              <select
                className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                {...register("istituto_vigilanza_id")}
              >
                <option value="">Nessuno</option>
                {istituti.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">Note interne</label>
            <textarea
              rows={3}
              className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
              {...register("note")}
            />
          </div>

          {/* Sola lettura: ruolo, creazione, ultimo accesso */}
          <div className="rounded-lg border border-line bg-ink/[0.02] p-3 text-sm">
            <dl className="flex flex-col gap-2">
              <div className="flex justify-between gap-4">
                <dt className="text-ink/50">Ruolo</dt>
                <dd className="text-right font-medium text-ink">{ROLE_LABELS[user.role]}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink/50">Creazione</dt>
                <dd className="text-right text-ink">{new Date(user.created_at).toLocaleString("it-IT")}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink/50">Ultimo accesso</dt>
                <dd className="text-right text-ink">
                  {user.last_login ? new Date(user.last_login).toLocaleString("it-IT") : "Mai"}
                </dd>
              </div>
            </dl>
            <p className="mt-2 text-xs text-ink/40">
              Il ruolo non è modificabile da qui: cambiarlo modifica permessi e visibilità dei dati e richiederà un
              flusso dedicato.
            </p>
          </div>
        </form>
      )}
    </Modal>
  );
}

// ============================================================================
// Modal — Flusso A: invito (solo admin)
// ============================================================================
function InviteModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const { showToast } = useToast();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateInvitoPayload>();

  const selectedRole = watch("role");

  const [centri, setCentri] = useState<CentroAssistenzaOption[]>([]);
  const [istituti, setIstituti] = useState<IstitutoVigilanzaOption[]>([]);
  // Ruolo cliente (Fase 3.3): selezione multipla di aziende, gestita fuori da
  // react-hook-form (è un array di oggetti) e iniettata come cliente_ids.
  const [clientiSelezionati, setClientiSelezionati] = useState<ClienteLite[]>([]);

  useEffect(() => {
    if (!open) return;
    setClientiSelezionati([]);
    anagraficaApi.listCentriAssistenza().then(({ centri }) => setCentri(centri));
    anagraficaApi.listIstitutiVigilanza().then(({ istituti }) => setIstituti(istituti));
  }, [open]);

  async function onSubmit(values: CreateInvitoPayload) {
    if (values.role === "cliente" && clientiSelezionati.length === 0) {
      showToast("Seleziona almeno un cliente da collegare.", "error");
      return;
    }
    try {
      const payload: CreateInvitoPayload =
        values.role === "cliente"
          ? { email: values.email, role: "cliente", cliente_ids: clientiSelezionati.map((c) => c.id) }
          : values;
      const result = await usersApi.createInvito(payload);
      showToast(result.message, result.email_sent ? "success" : "warning");
      reset();
      setClientiSelezionati([]);
      onClose();
      onCreated();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile creare l'invito."), "error");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invita un nuovo utente"
      size="sm"
      footer={<FooterButtons formId="invite-form" onCancel={onClose} loading={isSubmitting} submitLabel="Invia invito" />}
    >
      <form id="invite-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <Input
          label="Email"
          type="email"
          required
          error={errors.email?.message}
          {...register("email", { required: "L'email è obbligatoria." })}
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">
            Ruolo <span className="text-accent">*</span>
          </label>
          <select
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            {...register("role", { required: true })}
          >
            <option value="admin">Admin</option>
            <option value="staff">Staff Evo</option>
            <option value="centro_assistenza">Centro Assistenza</option>
            <option value="istituto_vigilanza">Istituto di Vigilanza</option>
            <option value="cliente">Cliente</option>
          </select>
        </div>

        {selectedRole === "centro_assistenza" && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">
              Centro Assistenza <span className="text-accent">*</span>
            </label>
            <select
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              {...register("centro_assistenza_id", { required: true })}
            >
              <option value="">Seleziona…</option>
              {centri.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedRole === "istituto_vigilanza" && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">
              Istituto di Vigilanza <span className="text-accent">*</span>
            </label>
            <select
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              {...register("istituto_vigilanza_id", { required: true })}
            >
              <option value="">Seleziona…</option>
              {istituti.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.nome}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedRole === "cliente" && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">
              Aziende collegate <span className="text-accent">*</span>
            </label>
            <ClientiMultiSelect selected={clientiSelezionati} onChange={setClientiSelezionati} />
            <p className="text-xs text-ink/50">
              L'utente vedrà in sola lettura i dati di queste aziende. Puoi collegarne più di una.
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
}

// ============================================================================
// Modal — Flusso B: creazione diretta operatore
// ============================================================================
function CreateOperatorModal({
  open,
  onClose,
  onCreated,
  callerRole,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  callerRole?: Role;
}) {
  const { showToast } = useToast();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateOperatorPayload>();

  const isAdmin = callerRole === "admin";
  const fixedRole = callerRole === "centro_assistenza" ? "operatore_tecnico" : callerRole === "istituto_vigilanza" ? "operatore_vigilanza" : undefined;
  const selectedRuolo = watch("ruolo");
  const watchedEmail = watch("email");
  const watchedTelefono = watch("telefono");
  // La scelta del metodo di accesso serve solo quando l'admin fornisce ENTRAMBI
  // i canali: con uno solo il backend lo deduce da sé.
  const showLoginMethodChoice = !!watchedEmail?.trim() && !!watchedTelefono?.trim();

  const [centri, setCentri] = useState<CentroAssistenzaOption[]>([]);
  const [istituti, setIstituti] = useState<IstitutoVigilanzaOption[]>([]);

  useEffect(() => {
    if (!open || !isAdmin) return;
    anagraficaApi.listCentriAssistenza().then(({ centri }) => setCentri(centri));
    anagraficaApi.listIstitutiVigilanza().then(({ istituti }) => setIstituti(istituti));
  }, [open, isAdmin]);

  async function onSubmit(values: CreateOperatorPayload) {
    try {
      const payload: CreateOperatorPayload = { ...values, ruolo: fixedRole ?? values.ruolo };
      const result = await usersApi.createOperator(payload);
      if (result.temp_password) {
        // Stesso caso del reset password: la credenziale compare qui e solo
        // qui, quindi il banner resta finché non lo si chiude.
        showToast(
          `Operatore creato. Password temporanea da comunicare manualmente: ${result.temp_password}`,
          "warning",
          { persistent: true, copyValue: result.temp_password }
        );
      } else {
        const canali = [result.credenziali_inviate.email && "email", result.credenziali_inviate.sms && "SMS"].filter(
          Boolean
        );
        showToast(`Operatore creato. Credenziali inviate via ${canali.join(" e ")}.`, "success");
      }
      reset();
      onClose();
      onCreated();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile creare l'operatore."), "error");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Aggiungi operatore"
      size="sm"
      footer={<FooterButtons formId="operator-form" onCancel={onClose} loading={isSubmitting} submitLabel="Crea operatore" />}
    >
      <form id="operator-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <Input
          label="Nome e cognome"
          required
          error={errors.nome?.message}
          {...register("nome", { required: "Il nome è obbligatorio." })}
        />

        <Input label="Email" type="email" hint="Facoltativa se fornisci un numero di telefono." {...register("email")} />
        <Input
          label="Telefono"
          hint="Facoltativo se fornisci un'email. Usato per l'invio SMS e come accesso. In formato internazionale: es. +393351234567."
          {...register("telefono")}
        />

        {showLoginMethodChoice && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">Metodo di accesso</label>
            <div className="flex gap-4 text-sm text-ink">
              <label className="flex items-center gap-2">
                <input type="radio" value="email" defaultChecked {...register("login_method")} />
                Email
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" value="telefono" {...register("login_method")} />
                Telefono
              </label>
            </div>
            <p className="text-xs text-ink/40">
              Con quale credenziale l'operatore effettuerà il login. L'altro campo resta solo per contatto/notifiche.
            </p>
          </div>
        )}

        {isAdmin && (
          <>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">
                Ruolo <span className="text-accent">*</span>
              </label>
              <select
                className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                {...register("ruolo", { required: true })}
              >
                <option value="operatore_tecnico">Operatore Tecnico</option>
                <option value="operatore_vigilanza">Operatore Vigilanza</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">
                {selectedRuolo === "operatore_vigilanza" ? "Istituto di Vigilanza" : "Centro Assistenza"}{" "}
                <span className="text-accent">*</span>
              </label>
              <select
                className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                {...register("parent_id", { required: "Obbligatorio quando crei come admin." })}
              >
                <option value="">Seleziona…</option>
                {(selectedRuolo === "operatore_vigilanza" ? istituti : centri).map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.nome}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
}

function FooterButtons({
  formId,
  onCancel,
  loading,
  submitLabel,
}: {
  formId: string;
  onCancel: () => void;
  loading: boolean;
  submitLabel: string;
}) {
  return (
    <div className="mt-2 flex justify-end gap-2">
      <Button type="button" variant="ghost" onClick={onCancel}>
        Annulla
      </Button>
      <Button type="submit" form={formId} loading={loading}>
        {submitLabel}
      </Button>
    </div>
  );
}

// ============================================================================
// Modal — aziende collegate a un utente cliente (Fase 3.3, solo admin).
// Carica le aziende attuali (GET /api/users/:id/clienti) e salva l'elenco
// completo (PUT), che il backend traduce in add/remove su user_clienti. La
// vista del cliente si aggiorna di conseguenza al suo prossimo caricamento.
// ============================================================================
function ClientiUserModal({ user, onClose }: { user: User | null; onClose: () => void }) {
  const { showToast } = useToast();
  const [selected, setSelected] = useState<ClienteLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      setSelected([]);
      return;
    }
    setLoading(true);
    usersApi
      .getUserClienti(user.id)
      .then(setSelected)
      .catch((err) => showToast(getErrorMessage(err, "Impossibile caricare le aziende collegate."), "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function handleSave() {
    if (!user) return;
    setSaving(true);
    try {
      await usersApi.setUserClienti(
        user.id,
        selected.map((c) => c.id)
      );
      showToast("Aziende collegate aggiornate.", "success");
      onClose();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare le aziende collegate."), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={user !== null}
      onClose={onClose}
      title={user ? `Aziende collegate — ${user.full_name ?? user.email}` : "Aziende collegate"}
      size="sm"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="button" onClick={handleSave} loading={saving} disabled={loading}>
            Salva
          </Button>
        </div>
      }
    >
      {user && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink/50">
            L'utente vede in sola lettura installazioni, interventi e attivazioni di queste aziende. Aggiungi o rimuovi
            aziende: le modifiche hanno effetto dal suo prossimo accesso alle liste.
          </p>
          {loading ? (
            <p className="text-sm text-ink/50">Caricamento…</p>
          ) : (
            <ClientiMultiSelect selected={selected} onChange={setSelected} />
          )}
          {!loading && selected.length === 0 && (
            <p className="text-xs text-danger">
              Nessuna azienda collegata: l'utente non vedrà alcun record finché non ne aggiungi almeno una.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

// ============================================================================
// Modal — permessi utente (Fase 2.4, solo admin). Mostra i permessi di DEFAULT
// del ruolo in sola lettura e gli override per-utente (concessi/negati)
// modificabili. Le chiavi valide arrivano dal catalogo centralizzato
// GET /api/permissions (non hardcodate): se in futuro si aggiungono permessi la
// UI si aggiorna da sola. L'override ha sempre la precedenza sul default del
// ruolo. Dopo ogni modifica la lista si aggiorna senza refresh manuale.
// La protezione vera è lato backend (requirePermission("utenti:amministrazione")
// su /api/permissions e /api/users/:id/permission-overrides); qui il bottone e
// il pannello sono solo comodità: nascondere la UI non basta.
// ============================================================================
function PermissionsModal({ user, onClose }: { user: User | null; onClose: () => void }) {
  const { showToast } = useToast();
  const [catalog, setCatalog] = useState<usersApi.PermissionsCatalog | null>(null);
  const [overrides, setOverrides] = useState<usersApi.PermissionOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [newKey, setNewKey] = useState("");
  const [newGranted, setNewGranted] = useState<"true" | "false">("true");

  useEffect(() => {
    if (!user) {
      setCatalog(null);
      setOverrides([]);
      setNewKey("");
      setNewGranted("true");
      return;
    }
    setLoading(true);
    Promise.all([usersApi.getPermissionsCatalog(), usersApi.listPermissionOverrides(user.id)])
      .then(([cat, ovr]) => {
        setCatalog(cat);
        setOverrides(ovr.overrides);
      })
      .catch((err) => showToast(getErrorMessage(err, "Impossibile caricare i permessi."), "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function refetchOverrides() {
    if (!user) return;
    const { overrides } = await usersApi.listPermissionOverrides(user.id);
    setOverrides(overrides);
  }

  async function handleAdd() {
    if (!user || !newKey) return;
    setSavingKey(newKey);
    try {
      await usersApi.setPermissionOverride(user.id, newKey, newGranted === "true");
      await refetchOverrides();
      setNewKey("");
      setNewGranted("true");
      showToast("Override salvato.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare l'override."), "error");
    } finally {
      setSavingKey(null);
    }
  }

  async function handleToggle(o: usersApi.PermissionOverride) {
    if (!user) return;
    setSavingKey(o.permission_key);
    try {
      await usersApi.setPermissionOverride(user.id, o.permission_key, !o.granted);
      await refetchOverrides();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile modificare l'override."), "error");
    } finally {
      setSavingKey(null);
    }
  }

  async function handleRemove(key: string) {
    if (!user) return;
    setSavingKey(key);
    try {
      await usersApi.deletePermissionOverride(user.id, key);
      await refetchOverrides();
      showToast("Override rimosso: l'utente torna al default del ruolo.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile rimuovere l'override."), "error");
    } finally {
      setSavingKey(null);
    }
  }

  const roleDefaults = user && catalog ? catalog.role_defaults[user.role] ?? [] : [];
  const defaultSet = new Set(roleDefaults);
  const overridesByKey = new Map(overrides.map((o) => [o.permission_key, o]));
  // Nel dropdown "aggiungi" mostriamo solo le chiavi non ancora derogate: per
  // cambiare un override esistente si usa il toggle nella lista sopra.
  const addableKeys = catalog ? catalog.permissions.filter((k) => !overridesByKey.has(k)) : [];

  return (
    <Modal
      open={user !== null}
      onClose={onClose}
      title={user ? `Permessi — ${user.full_name ?? user.email}` : "Permessi"}
      size="lg"
      footer={
        <div className="flex w-full justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Chiudi
          </Button>
        </div>
      }
    >
      {user && (
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-ink/50">Ruolo</span>
            <Badge color="primary">{ROLE_LABELS[user.role]}</Badge>
          </div>

          {loading ? (
            <p className="text-sm text-ink/50">Caricamento permessi…</p>
          ) : (
            <>
              {/* Override attivi — modificabili */}
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-ink">Override attivi</h3>
                <p className="text-xs text-ink/50">
                  Deroghe rispetto al ruolo, con precedenza sul default. Il pulsante di stato inverte
                  concesso/negato; la ✕ rimuove l'override e ripristina il comportamento del ruolo.
                </p>
                {overrides.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-line px-3 py-4 text-center text-sm text-ink/40">
                    Nessun override: l'utente segue interamente i permessi del ruolo.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {overrides.map((o) => {
                      const isDefault = defaultSet.has(o.permission_key);
                      const redundant = isDefault === o.granted; // override uguale al default del ruolo
                      return (
                        <li
                          key={o.id}
                          className="flex items-center justify-between gap-3 rounded-lg border border-line bg-white px-3 py-2"
                        >
                          <div className="flex min-w-0 flex-col">
                            <code className="truncate text-sm text-ink">{o.permission_key}</code>
                            {redundant && (
                              <span className="text-xs text-ink/40">
                                Coincide con il default del ruolo (nessun effetto pratico).
                              </span>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleToggle(o)}
                              disabled={savingKey === o.permission_key}
                              title="Inverti concesso/negato"
                              className="rounded-md transition hover:opacity-80 disabled:opacity-50"
                            >
                              <Badge color={o.granted ? "success" : "danger"}>
                                {o.granted ? "Concesso" : "Negato"}
                              </Badge>
                            </button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemove(o.permission_key)}
                              loading={savingKey === o.permission_key}
                              aria-label={`Rimuovi override ${o.permission_key}`}
                            >
                              <X className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {/* Aggiungi override — dropdown di chiavi valide, non testo libero */}
              <section className="flex flex-col gap-2 rounded-lg border border-line bg-ink/[0.02] p-3">
                <h3 className="text-sm font-semibold text-ink">Aggiungi override</h3>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="flex flex-1 flex-col gap-1.5">
                    <label className="text-xs font-medium text-ink/70">Permesso</label>
                    <select
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                    >
                      <option value="">Seleziona un permesso…</option>
                      {addableKeys.map((k) => (
                        <option key={k} value={k}>
                          {k}
                          {defaultSet.has(k) ? "  ·  (default del ruolo)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-ink/70">Azione</label>
                    <select
                      value={newGranted}
                      onChange={(e) => setNewGranted(e.target.value as "true" | "false")}
                      className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                    >
                      <option value="true">Concedi</option>
                      <option value="false">Nega</option>
                    </select>
                  </div>
                  <Button
                    type="button"
                    onClick={handleAdd}
                    loading={newKey !== "" && savingKey === newKey}
                    disabled={!newKey}
                  >
                    Aggiungi
                  </Button>
                </div>
              </section>

              {/* Permessi di default del ruolo — sola lettura */}
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-ink">Permessi di default del ruolo</h3>
                <p className="text-xs text-ink/50">
                  Sola lettura. Derivano dal ruolo {ROLE_LABELS[user.role]} e valgono salvo override. Un permesso
                  barrato è stato negato da un override.
                </p>
                {roleDefaults.length === 0 ? (
                  <p className="text-sm text-ink/40">Questo ruolo non ha permessi di default.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {roleDefaults.map((k) => {
                      const overridden = overridesByKey.get(k);
                      const denied = overridden && !overridden.granted;
                      return (
                        <span
                          key={k}
                          className={
                            denied
                              ? "rounded-md border border-danger/30 bg-danger/5 px-2 py-1 text-xs text-danger line-through"
                              : "rounded-md border border-line bg-white px-2 py-1 text-xs text-ink/70"
                          }
                        >
                          {k}
                        </span>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
